import { createContext, useContext } from 'react'

import Loading from 'modules/layout/components/Page/Loading'
import { ProjectTimeRecords } from 'modules/project_management/hooks/projectTimeRecord/useGetProjectTimeRecords'
import { ProjectWorkers } from 'modules/project_management/hooks/projectWorker/useGetProjectsAssignedToWorker'
import { type IUseGetEmployeeProjectsTrackingMonthReturn } from 'modules/project_management/hooks/useGetEmployeeProjectsTrackingByMonth'

const EmployeeProjectsTrackingContext =
  createContext<IUseGetEmployeeProjectsTrackingMonthReturn | null>(null)

export const EmployeeProjectsTrackingProvider = ({
  children,
  projectsAssigned,
  timeRecords,
  hasProjectsToTrack,
  fetchingTimeRecords,
  fetchingProjectWorkers,
  refetchEmployeeTimeRecords,
}: React.PropsWithChildren<{
  projectsAssigned: ProjectWorkers
  timeRecords: ProjectTimeRecords
  hasProjectsToTrack: boolean
  fetchingTimeRecords: boolean
  fetchingProjectWorkers: boolean
  refetchEmployeeTimeRecords: () => Promise<void>
}>) => {
  if (fetchingTimeRecords || fetchingProjectWorkers) return <Loading />

  return (
    <EmployeeProjectsTrackingContext.Provider
      value={{
        queryData: {
          projectsAssigned,
          timeRecords,
          hasProjectsToTrack,
          fetchingTimeRecords,
          fetchingProjectWorkers,
          refetchEmployeeTimeRecords,
        },
        getProjectNameForShift: (attendanceShiftId) =>
          timeRecords.find((tr) => tr.attendanceShiftId === attendanceShiftId)?.projectWorker
            ?.imputableProject?.name || null,
        getSubprojectNameForShift: (attendanceShiftId) =>
          timeRecords.find((tr) => tr.attendanceShiftId === attendanceShiftId)?.subproject?.name ||
          null,
      }}
    >
      {children}
    </EmployeeProjectsTrackingContext.Provider>
  )
}

export const useEmployeeProjectsTracking = () => {
  const context = useContext(EmployeeProjectsTrackingContext)

  if (context === undefined) {
    throw new Error(
      'useEmployeeProjectsTracking must be used within a EmployeeProjectsTrackingProvider'
    )
  }

  return context
}
